import { getTracking, getUserBackground, setTracking, updateTrackingValues, updateUserBackground } from "@/services/orbbyte";
import appStore from "../store"
import { formatBackground, formatDateUtil, translate } from "../format";
import { getDocById } from "@/services/firebase";
import { mapAndStoreUserPhenotype } from "@/services/user";
import { logEvent } from "../logs";
import { goTo } from "../router";
import { CalooAdminConfig } from "@/services/admin";

export async function initUser() {
    const user = appStore.get('user') || null;
    if (user) {
        // console.log('User already initialized:', user);
        const info = await getDocById('users', user.firebaseUser ? user.firebaseUser : user.uid);
        if (info) {
            appStore.set('user', { ...user, ...info });
            //   console.log('User info updated:', appStore.get('user'));
        } else {
            console.warn('No user info found for:', user);
        }
        return;
    }
}

export function getUser() {
    return appStore.get('user') || null;
}

export async function updateLocalUser() {
    const userInfo = await getBasicInfo();
    if (userInfo) {
        appStore.set("user", userInfo);
    }
}

export async function getBasicInfo() {
    const user = appStore.get('user') || null;
    try {
        console.log('Fetching user info for:', user);
        if (!user) {
            console.warn('No user found in store.');
            return null;
        }
        const info = await getDocById('users', user.firebaseUser ? user.firebaseUser : user.uid);
        console.log('info', info);
        return info
            ? {
                ...user,
                ...info,
            }
            : null;
    } catch (error) {
        logEvent(`Error fetching user info: ${JSON.stringify(error)}`);
        goTo('/login');
        return null;
    }
}

export async function updateDailyTracking(key: string, value: number, unit: string) {
    try {
        const user = appStore.get("user");
        if (!user?.idPost) {
            console.warn("No user idPost found.");
            return;
        }

        // Cargar tracking actual
        const log = await getTracking({ relatedPost: user.idPost });
        const dateToday = formatDateUtil(new Date(), "yyyy-MM-dd");

        // Buscar si ya existe un tracking con esa key
        const index = log.data.findIndex((el) => el.key === key);

        if (index === -1) {
            // No existe, creamos nuevo
            console.warn(`No tracking for '${key}' found, creating a new one.`);
            await createLog(
                {
                    title: translate(key.toUpperCase(), key),
                    units: unit,
                    values: [[dateToday, value]],
                    isActive: true,
                    idPost: null,
                    key,
                    minValue: "0",
                    maxValue: "50000",
                },
                user.idPost
            );
        } else {
            // Existe: añadir o actualizar
            const values = log.data[index].values || [];
            const existingIndex = values.findIndex((v) => v[0] === dateToday);

            if (existingIndex === -1) {
                values.push([dateToday, value]);
            } else {
                values[existingIndex][1] = value;
            }

            await updateTrackingValues({
                data: { values },
                idPost: log.data[index].idPost,
            });
        }
    } catch (error) {
        console.error(`❌ Error updating tracking for '${key}':`, error);
        throw error;
    }
}

export async function updateWeight(weight: number) {
    try {
        const userNutritional = { ...(await getBackground()) };
        if (!userNutritional) {
            console.warn('No user nutritional info found, initializing with default values.');
            return;
        }
        // update current Background
        userNutritional.weight = weight;
        await updateBackground({ ...userNutritional });
        const user = appStore.get('user') || null;
        const log = await getTracking({ relatedPost: user.idPost });
        // console.log('Current weight tracking log:', log);
        const indexWeight = log.data.findIndex((el) => el.key === 'weight');
        // console.log('Index of weight tracking:', indexWeight);
        if (indexWeight === -1) {
            console.warn('No weight tracking found, creating a new one.');
            await createLog({ title: translate("Weight", "Peso"), units: 'Kg', values: [[formatDateUtil(new Date, 'yyyy-MM-dd'), weight]], isActive: true, idPost: null, key: 'weight', minValue: '20', maxValue: '400' }, user.idPost);
            return;
        }
        const dateToday = formatDateUtil(new Date, 'yyyy-MM-dd');
        let index = log.data[indexWeight]?.values?.findIndex((el) => dateToday === el[0]);
        // console.log('Index of today\'s weight in tracking:', index);
        if (index === -1) {
            log.data[indexWeight]?.values?.push([formatDateUtil(new Date, 'yyyy-MM-dd'), weight]);
        } else {
            if (log.data[indexWeight] && log.data[indexWeight].values && log.data[indexWeight].values[index]) {
                // console.log('Updating existing weight value at index:', index, 'with weight:', weight, log.data[indexWeight]?.idPost);
                log.data[indexWeight].values[index][1] = weight; // update existing value
            }
        }
        updateTrackingValues({ data: { values: log.data[indexWeight].values }, idPost: log.data[indexWeight]?.idPost });
        // console.log('Weight updated:', weight);
    } catch (error) {
        console.error('Error updating weight:', error);
        throw error; // Re-throw to handle it in the calling function if needed
    }
}

async function createLog(log: any, idPost: string) {
    const tracking = {
        parameter: log.title,
        units: log.units,
        values: log.values || [[formatDateUtil(new Date, 'yyyy-MM-dd'), 0]],
        isActive: true,
        idPost: null,
        key: log.key,
        minValue: log.minValue,
        maxValue: log.maxValue,
    }
    const res = await setTracking({ data: { ...tracking, relatedPost: idPost, postName: log.key } });
    // console.log('Tracking created: ', log.title, idPost, JSON.stringify(res));
    return { ...tracking, idPost: res?.idPost || null };
}

export async function getBackground() {
    const user = appStore.get('user') || null;
    // console.log(user);

    if (!user || !user.idPost) {
        return null;
    }
    return formatBackground((await getUserBackground({ idPost: user?.idPost }))?.data[0]) || null;
}

export async function updateBackground(data: any) {
    const user = appStore.get('user') || null;

    if (!user || !user.idPost) {
        return null;
    }

    const userBck = { ...formatBackground(data, "update") };
    mapAndStoreUserPhenotype(userBck);

    const res = (await updateUserBackground({ data: userBck, idPost: user?.idPost }));

    return res?.statusCode === 200;

}

export async function uploadFotoSE(uid: any, data: any) {
    try {
        const BASE_URL =
            CalooAdminConfig?.googleApi ||
            import.meta.env.VITE_MEEFOD_API_URL;

        const response = await fetch(`${BASE_URL}/uploadfoto/logo`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `${import.meta.env.VITE_MEFOOD_API_TOKEN}`
            },
            body: data,
        });
        // const response = await fetch(`https://app.mefood.io/uploadlogofoto/${uid}/${isIOS()}`, {
        //     method: 'POST',
        //     body: data
        // });
        // console.log('* uploadLogoFotoSE:', response);
        if (!response.ok) {
            console.log('* Error uploadLogoFotoSE:', response.statusText);
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.log('* Error uploadLogoFotoSE:', error);
    }
}

export function parseFullName(fullName: string) {
    try {
        if (!fullName || typeof fullName !== "string") {
            return {
                name: "",
                surname: "",
                username: ""
            };
        }

        const trimmed = fullName.trim().replace(/\s+/g, " ");
        const parts = trimmed.split(" ");

        const name = parts[0] || "";
        const surname = parts.slice(1).join(" ") || "";

        // Generar un username simple: nombre + primer apellido (sin espacios) en minúsculas
        let usernameBase = name + (surname ? surname.split(" ")[0] : "");
        usernameBase = usernameBase.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // quita acentos
        usernameBase = usernameBase.replace(/[^a-z0-9]/g, ""); // solo letras y números

        return {
            name,
            surname,
            username: usernameBase
        };
    } catch (error) {
        console.error("Error parsing full name:", error);
        return {
            name: "",
            surname: "",
            username: ""
        };
    }
}